import type { Optional } from '@festina-life/core'

import { isSameTabTarget } from './isSameTabTarget'
import type { TabLink } from './types'

/**
 * Applies a change made to the visible tab links onto the stored ones, so tabs
 * hidden by `filterExistentTabLinks` are kept instead of being dropped when the
 * visible tab links are written back.
 *
 * A stored tab that was visible is replaced by its next version, or removed when
 * it is no longer among the next visible tab links. A hidden tab is kept, but never
 * active since the current route is only tracked as a tab when its item exists.
 * Next visible tab links that are not stored yet are appended.
 */
export const mergeIntoStoredTabLinks = ({
  storedTabLinks,
  visibleTabLinks,
  nextVisibleTabLinks,
}: {
  storedTabLinks: Optional<TabLink[]>
  visibleTabLinks: Optional<TabLink[]>
  nextVisibleTabLinks: TabLink[]
}): TabLink[] => {
  const isCurrentlyVisible = (tabLink: TabLink) =>
    !!visibleTabLinks?.some((visibleTabLink) =>
      isSameTabTarget(visibleTabLink, tabLink),
    )
  const findNextVisible = (tabLink: TabLink) =>
    nextVisibleTabLinks.find((nextTabLink) =>
      isSameTabTarget(nextTabLink, tabLink),
    )

  const mergedTabLinks = (storedTabLinks ?? []).flatMap((storedTabLink) => {
    if (isCurrentlyVisible(storedTabLink)) {
      const nextTabLink = findNextVisible(storedTabLink)
      return nextTabLink ? [nextTabLink] : []
    }

    return [{ ...storedTabLink, isActive: false }]
  })

  const newTabLinks = nextVisibleTabLinks.filter(
    (nextTabLink) =>
      !mergedTabLinks.some((tabLink) => isSameTabTarget(tabLink, nextTabLink)),
  )

  return [...mergedTabLinks, ...newTabLinks]
}
